import {Component} from 'substance'

import './scss/configurationtesttool.scss'

import PluginListComponent from './PluginListComponent'
import PluginFormComponent from './PluginFormComponent'
import EditorComponent from './EditorComponent'

class ConfigurationTestToolComponent extends Component {

    constructor(...args) {
        super(...args)

        this.getValues = null // Get values from form
    }

    getInitialState() {
        // Get list of overridden plugin ids
        const overridden = this.context.api.settings.get('__global__', 'localConfigOverridden', [])

        // Get list of removed plugin confs
        const removed = this.context.api.settings.get('__global__', 'localConfigRemoved', [])

        // Get list of removed plugin confs
        const added = this.context.api.settings.get('__global__', 'localConfigAdded', [])

        // Retrieve current configuration
        const config = this.context.configurator.getConfiguration()

        // Copy config to an unmodified config
        const unmodifiedConfig = JSON.parse(JSON.stringify(config))

        return {
            selectedTab: 'Plugins',
            selectedPlugin: null,
            config,
            unmodifiedConfig,
            overridden,
            removed,
            added,
            src: null,
            srcSequence: 0
        }
    }

    /**
     * @param newProps
     * @param newState
     * @returns {Boolean}
     */
    shouldRerender(newProps, newState) {
        if (newState.srcSequence > this.state.srcSequence) {
            return false
        }

        return !newProps.saveInProgress
    }

    render($$) {
        this.tabs = []
        this._addTab('Plugins', () => this._renderPluginList($$))
        this._addTab('Tab List', () => this._renderTabsList($$))

        return $$('div').addClass('devtool_configtesttool').append([
            $$('div').addClass('devtool_sidebar left').append(
                this._renderTabs($$),
                this._renderTabContent($$)
            ),
            $$('div').addClass('devtool_sidebar right')
                .append(
                    this._renderFormContainer($$).append(
                        this._renderForm($$),
                        this._renderEditor($$)
                    )
                )
        ])
    }

    _renderTabContent($$) {
        return $$('div').addClass('devtool_tabcontent').append(this._renderSelectedTab($$))
    }

    /**
     * Renders added tabs and sets up click event
     *
     * @param $$
     * @private
     */
    _renderTabs($$) {
        return $$('div').addClass('devtool_tabs').append(
            this.tabs.map(({name}) => {
                const tabEl = $$('span').append(name)
                    .on('click', () => {
                        this.extendState({
                            selectedTab: name
                        })
                    })

                if (this.state.selectedTab === name) {
                    tabEl.addClass('active')
                }

                return tabEl
            })
        )
    }

    /**
     *
     * @param name Label which is rendered
     * @param render Render function which should return tab contents
     * @private
     */
    _addTab(name, render) {
        this.tabs.push({
            name,
            render
        })
    }

    /**
     * Calls the selected tab's render-function
     *
     * @private
     */
    _renderSelectedTab() {
        return this.tabs.find(({name}) => name === this.state.selectedTab).render()
    }

    /**
     * @param src
     * @private
     */
    _onContentChange(src) {
        this.extendState({
            src,
            srcSequence: this.state.srcSequence + 1
        })
    }

    /**
     * @param $$
     * @returns {VirtualElement}
     * @private
     */
    _renderFormContainer($$) {
        return $$('div').addClass('pluginlist__form-container')
    }

    /**
     * @param $$
     * @returns {VirtualElement}
     * @private
     */
    _renderEditor($$) {
        const data = this.state.selectedPlugin && this.state.selectedPlugin.data ? this.state.selectedPlugin.data : {}
        const json = JSON.stringify(data, null, '\t')

        return $$(EditorComponent, {
            source: json,
            onContentChange: (src, change) => {
                this._onContentChange(src, change)
            }
        })
    }

    /**
     * @param $$
     * @returns {VirtualElement}
     * @private
     */
    _renderForm($$) {
        return $$(PluginFormComponent, {
            plugin: this.state.selectedPlugin,
            setGetFormDataEndPoint: func => {
                this.getValues = func
            },
            getCurrentPluginConfig: () => { return this._currentPluginConfig }
        })
    }

    _renderTabsList($$) {
        return $$('div').addClass('devtool_tabslist').append(
            $$('p').append(this.getLabel('devtools-existing-tabs-description')),
            ...this.context.configurator.getOrderedSideBars().map((tab) => {
                return $$('p').append(
                    $$('strong').append(`${this.getLabel('Tab')}: ${tab.name}`)
                ).append($$('br')).append(`tab-id: ${tab.id}`)
            })
        )
    }

    /**
     * @param $$
     * @returns {VirtualElement}
     * @private
     */
    _renderPluginList($$) {
        return $$(PluginListComponent, {
            selectedPlugin: this.state.selectedPlugin,
            config: this.state.config,
            overridden: this.state.overridden,
            removed: this.state.removed,
            added: this.state.added,
            onSelected: plugin => {
                this._pluginSelected(plugin)
            },
            onRemoved: plugin => {
                this._pluginRemoved(plugin)
            },
            onAdded: (plugin, locally) => {
                this._pluginAdded(plugin, locally)
            },
            onDropped: (droppedId, onPluginId, addAfter) => {
                this._pluginDropped(droppedId, onPluginId, addAfter)
            }
        }).ref('pluginlist')
    }


    /**
     * Compare configurations, cannot be done using just JSON.stringify()
     * on the whole config object as the order of object keys differ without
     * actually being different.
     *
     * @param orig
     * @param edit
     * @returns {Boolean}
     * @private
     */
    _configDiffers(orig, edit) {
        // Make sure data property exists if not there
        if (!orig.data) {
            orig.data = {}
        }
        if (!edit.data) {
            edit.data = {}
        }

        // Basic check
        let origKeys = Object.keys(orig).sort()
        let editKeys = Object.keys(edit).sort()
        if (origKeys.join('') !== editKeys.join('')) {
            return true
        }

        // Deep check
        return origKeys.some((k) => JSON.stringify(edit[k]) !== JSON.stringify(orig[k]))
    }

    /**
     * When a new plugin has been selected in the list
     *
     * @param {object} plugin Full configuration object for plugin or null
     * @returns {Boolean}
     */
    _pluginSelected(plugin) {
        // Compare currently selected plugin with possible change
        if (!this.state.selectedPlugin) {
            this.extendState({
                selectedPlugin: plugin
            })
            return true
        }

        const cfg = this._currentPluginConfig
        if (!cfg) {
            return false
        }

        if (!this._configDiffers(this.state.selectedPlugin, cfg)) {
            this.extendState({
                selectedPlugin: plugin
            })
            return true
        }

        this._pluginChanged(plugin, cfg)
        return true
    }

    /**
     * Apply changes for a plugin to the configuration
     *
     * @param {object} plugin   Full configuration object for plugin
     * @param {object} cfg      New configuration object for this plugin
     * @private
     */
    _pluginChanged(plugin, cfg) {
        const selectedPlugin = this.state.selectedPlugin

        // Remove from overridden list (with old id) and add with new id
        const overridden = this.state.overridden.filter(id => id !== selectedPlugin.id)
        overridden.push(cfg.id)

        // Remove from added list (if there) with old id, and re-add with new id
        let added = this.state.added
        if (this.state.added.includes(selectedPlugin.id)) {
            added = this.state.added.filter(id => id !== selectedPlugin.id)
            added.push(cfg.id)
        }

        // Remove from removed list (if there) with old id, and re-add with new id
        let removed = this.state.removed
        if (this.state.removed.includes(selectedPlugin.id)) {
            removed = this.state.removed.filter(id => id !== selectedPlugin.id)
            removed.push(cfg.id)
        }

        // Replace plugin in config
        const config = this.state.config
        const pluginIndex = config.plugins.findIndex(({id}) => id === this.state.selectedPlugin.id)
        config.plugins[pluginIndex] = cfg

        this.extendState({
            overridden,
            added,
            config,
            selectedPlugin: plugin
        })
    }

    /**
     * @param plugin
     * @private
     */
    _pluginRemoved(plugin) {
        let added, removed
        if (this.state.added.includes(plugin.id)) {
            // Remove from added
            added = this.state.added.filter(p => plugin.id !== p.id)
            removed = this.state.removed
        }
        else {
            // Store in removed list
            removed = this.state.removed.filter(p => plugin.id !== p.id)
            removed.push(plugin)
            added = this.state.added
        }

        // Remove plugin from config
        const config = this.state.config
        config.plugins = config.plugins.filter(p => plugin.id !== p.id)

        const selPlugin = this.state.selectedPlugin
        this.extendState({
            removed,
            added,
            config,
            selectedPlugin: (selPlugin && selPlugin.id === plugin.id) ? null : selPlugin
        })
    }

    /**
     * @param plugin
     * @param addedLocally
     * @private
     */
    _pluginAdded(plugin, addedLocally) {
        // Remove from removed list
        const removed = this.state.removed.filter(p => plugin.id !== p.id)

        // If added locally (not existing in real config)
        const added = this.state.added
        if (addedLocally) {
            added.push(plugin.id)
        }

        // Add to config
        const config = this.state.config
        let i = 0
        if (this.state.selectedPlugin) {
            i = config.plugins.findIndex(({id}) => id === this.state.selectedPlugin.id)
        }

        config.plugins.splice(i, 0, plugin)

        this.extendState({
            removed,
            added,
            config
        })

        setTimeout(() => {
            this._pluginSelected(plugin)
        })
    }

    /**
     * @param fromId
     * @param toId
     * @param addAfter
     * @private
     */
    _pluginDropped(fromId, toId, addAfter) {
        if (!fromId) {
            return
        }

        const config = this.state.config

        const targetIndex = config.plugins.findIndex(({id}) => id === toId)
        const toIndex = addAfter ? targetIndex + 1 : targetIndex
        const fromIndex = config.plugins.findIndex(({id}) => id === fromId)
        const fromPlugin = config.plugins[fromIndex]

        config.plugins[fromIndex] = null
        config.plugins.splice(toIndex, 0, fromPlugin)
        config.plugins = config.plugins.filter(n => n)

        this.extendState({
            config: config
        })
    }

    /**
     * @returns {Object|null}
     * @private
     */
    get _source() {
        try {
            return (JSON.parse(this.state.src))
        }
        catch (err) {
            console.error(err.message)
            return null
        }
    }

    get _currentPluginConfig() {
        if (!this.state.selectedPlugin) {
            return null
        }

        const cfg = this.getValues()
        cfg.data = this._source

        if (cfg.data === null) {
            // eslint-disable-next-line no-alert
            window.alert('Configuration is not valid JSON, please correct before moving on')
            return null
        }

        return cfg
    }

    /**
     * Checks if any plugins are modified, removed, or added
     *
     * @returns {boolean}
     * @private
     */
    get _isConfigModified() {
        return this._isOrderModified || (this.state.overridden.length + this.state.removed.length + this.state.added.length) > 0
    }

    /**
     * @returns {boolean}
     * @private
     */
    get _isOrderModified() {
        const pluginOrder = this.state.config.plugins.map(({id}) => id)
        const originalPluginOrder = this.state.unmodifiedConfig.plugins.map(({id}) => id)

        return pluginOrder.join('') !== originalPluginOrder.join('')
    }

    /**
     * Close the config editor and, if wanted, save and apply config changes
     * Clear all button is handled in ConfigurationTestToolCommand
     *
     * @param {string} status
     * @private
     */
    onClose(status) {
        if (status === 'save') {
            if (this._pluginSelected(null) === false) {
                return false
            }

            // Create local configuration or empty it
            const localConfiguration = this._isConfigModified ? this.state.config : null
            this.context.api.settings.set(
                '__global__',
                'localConfiguration',
                localConfiguration
            )

            this.context.api.settings.set(
                '__global__',
                'localConfigOverridden',
                this.state.overridden
            )

            this.context.api.settings.set(
                '__global__',
                'localConfigRemoved',
                this.state.removed
            )

            this.context.api.settings.set(
                '__global__',
                'localConfigAdded',
                this.state.added
            )

            this.context.api.browser.reload()
        }

        return true
    }
}

export default ConfigurationTestToolComponent
